import type { HarborPropKind } from '@/meshkit/props/harbor';
import type { VehicleKind } from '@/meshkit/vehicles/cars';
import type {
  BuildingPlacement,
  BridgePlacement,
  LevelData,
  ParkedVehiclePlacement,
  PropPlacement,
  StreetlampPlacement,
  TrafficRoute,
} from '@/world/LevelLoader';
import { zone, type LevelZone, type LevelZoneKind } from '@/world/MapSemantics';
import type { Obstacle } from '@/world/collision';

export interface RoadBlueprint {
  readonly id: string;
  readonly x: number;
  readonly z: number;
  readonly halfX: number;
  readonly halfZ: number;
  readonly intent: string;
  readonly route?: TrafficRoute;
}

export interface BuildingBlockBlueprint {
  readonly id: string;
  readonly intent: string;
  readonly buildings: readonly BuildingPlacement[];
}

export interface AuthoredZoneBlueprint {
  readonly id: string;
  readonly kind: Exclude<LevelZoneKind, 'roadLane'>;
  readonly x: number;
  readonly z: number;
  readonly halfX: number;
  readonly halfZ: number;
  readonly intent: string;
  readonly allowedProps?: readonly HarborPropKind[];
  readonly allowedVehicles?: readonly VehicleKind[];
  readonly clearance?: number;
}

export interface PropClusterBlueprint {
  readonly id: string;
  readonly zoneId: string;
  readonly intent: string;
  readonly props: readonly PropPlacement[];
}

export interface DistrictBlueprint {
  readonly id: string;
  readonly levelName: string;
  readonly districtName: string;
  readonly primaryRoute: string;
  readonly landmarks: readonly string[];
  readonly ground: LevelData['ground'];
  readonly quayWall?: LevelData['quayWall'];
  readonly quayDeck?: LevelData['quayDeck'];
  readonly water?: LevelData['water'];
  readonly obstacles: readonly Obstacle[];
  readonly bridges?: readonly BridgePlacement[];
  readonly roads: readonly RoadBlueprint[];
  readonly pedestrianZones: readonly AuthoredZoneBlueprint[];
  readonly buildingBlocks: readonly BuildingBlockBlueprint[];
  readonly streetlamps: readonly StreetlampPlacement[];
  readonly propClusters: readonly PropClusterBlueprint[];
  readonly parkedVehicles?: readonly ParkedVehiclePlacement[];
  readonly safehouse: LevelData['safehouse'];
}

export function levelDataFromBlueprint(blueprint: DistrictBlueprint): LevelData {
  const buildings = blueprint.buildingBlocks.flatMap((block) => block.buildings);
  const props = blueprint.propClusters.flatMap((cluster) => cluster.props);
  const authoredZones = blueprint.pedestrianZones.map(zoneFromBlueprint);
  const roadZones = blueprint.roads.map((road) => zone(road.id, 'roadLane', road.x, road.z, road.halfX, road.halfZ, road.intent));
  const buildingZones = blueprint.buildingBlocks.flatMap((block) => block.buildings.map((building) => zone(
    `${blueprint.id}-building-${building.seed}`,
    'buildingFootprint',
    building.x,
    building.z,
    building.width / 2,
    building.depth / 2,
    block.intent,
    { allowedProps: ['neonSign'] },
  )));
  const trafficRoutes = [
    ...blueprint.roads.map((road) => road.route).filter((route): route is TrafficRoute => route !== undefined),
  ];

  const level: LevelData = {
    name: blueprint.levelName,
    ground: blueprint.ground,
    ...(blueprint.quayWall ? { quayWall: blueprint.quayWall } : {}),
    ...(blueprint.quayDeck ? { quayDeck: blueprint.quayDeck } : {}),
    ...(blueprint.water ? { water: blueprint.water } : {}),
    obstacles: blueprint.obstacles,
    ...(blueprint.bridges ? { bridges: blueprint.bridges } : {}),
    buildings,
    streetlamps: blueprint.streetlamps,
    props,
    trafficRoutes,
    parkedVehicles: blueprint.parkedVehicles ?? [],
    semantics: {
      districtName: blueprint.districtName,
      primaryRoute: blueprint.primaryRoute,
      landmark: blueprint.landmarks[0] ?? blueprint.districtName,
      zones: [...roadZones, ...authoredZones, ...buildingZones],
    },
    safehouse: blueprint.safehouse,
  };
  return level;
}

export function blueprintZoneIds(blueprint: DistrictBlueprint): Set<string> {
  return new Set([
    ...blueprint.roads.map((road) => road.id),
    ...blueprint.pedestrianZones.map((zoneValue) => zoneValue.id),
  ]);
}

export function clusterZonesExist(blueprint: DistrictBlueprint): boolean {
  const ids = blueprintZoneIds(blueprint);
  return blueprint.propClusters.every((cluster) => ids.has(cluster.zoneId));
}

function zoneFromBlueprint(input: AuthoredZoneBlueprint): LevelZone {
  const options: {
    allowedProps?: readonly HarborPropKind[];
    allowedVehicles?: readonly VehicleKind[];
    clearance?: number;
  } = {};
  if (input.allowedProps) options.allowedProps = input.allowedProps;
  if (input.allowedVehicles) options.allowedVehicles = input.allowedVehicles;
  if (input.clearance !== undefined) options.clearance = input.clearance;
  return zone(input.id, input.kind, input.x, input.z, input.halfX, input.halfZ, input.intent, options);
}
